import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import useRBAC from "../hooks/useRBAC";
import { callApi } from "../api/api";

import {
  Users,
  Shield,
  Database,
  Link2,
  GitBranch,
  ClipboardList,
  FileSearch,
  Tags,
} from "lucide-react";

export default function Manage() {
  const navigate = useNavigate();
  const { hasAccess } = useRBAC();
  const { instance, accounts } = useMsal();

  const [stats, setStats] = useState({
    users: 0,
    roles: 0,
    resources: 0,
    userRoles: 0,
    roleResources: 0,
  });


  /* ================= LOAD STATS ================= */
  const loadStats = useCallback(async () => {
    try {
      if (!accounts.length) return;

      const res = await callApi(
        instance,
        accounts,
        "GET",
        "/api/admin/dashboard",
      );

      setStats(res?.data?.data?.stats || {});
    } catch (err) {
      console.error("Failed to load stats", err);
    }
  }, [instance, accounts]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  /* ================= CARDS ================= */
  const cards = [
    {
      title: "Users",
      desc: "View, add, and manage portal users",
      icon: Users,
      path: "/users",
      resource: "ADMIN",
      access: "READ",
      count: stats.users,
    },
    {
      title: "Roles",
      desc: "Define roles and their descriptions",
      icon: Shield,
      path: "/roles",
      resource: "ADMIN",
      access: "READ",
      count: stats.roles,
    },
    {
      title: "Resources",
      desc: "Manage protected resources & access levels",
      icon: Database,
      path: "/resources",
      resource: "ADMIN",
      access: "READ",
      count: stats.resources,
    },
    {
      title: "Lookup Management",
      desc: "Manage document types, lookup codes, LOBs and metadata values",
      icon: Tags,
      path: "/lookups",
      resource: "ADMIN",
      access: "READ",
      count: null,
    },
    {
      title: "User-Role Assign",
      desc: "Assign roles to users",
      icon: Link2,
      path: "/assign-user-role",
      resource: "ADMIN",
      access: "WRITE",
      count: stats.userRoles,
    },
    {
      title: "Role-Resource Assign",
      desc: "Map resources onto roles",
      icon: GitBranch,
      path: "/assign-role-resource",
      resource: "ADMIN",
      access: "WRITE",
      count: stats.roleResources,
    },
    {
      title: "Access Review",
      desc: "Inspect effective access for any user",
      icon: ClipboardList,
      path: "/access-review",
      resource: "ADMIN",
      access: "READ",
      count: null,
    },
    {
      title: "Audit Logs",
      desc: "Track system activity and access history",
      icon: FileSearch,
      path: "/audit-logs",
      resource: "ADMIN",
      access: "READ",
      count: null,
    },
  ];

  /* ================= UI ================= */
  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <div style={styles.breadcrumb}>Administration &gt; Manage</div>

          <h1 style={styles.title}>Manage</h1>

          <p style={styles.subtitle}>
            Administer users, roles, resources and their assignments.
          </p>
        </div>
      </div>

      {/* GRID */}
      <div style={styles.grid}>
        {cards
          .filter((c) => hasAccess(c.resource, c.access))
          .map((c, i) => {
            const Icon = c.icon;

            return (
              <div
                key={i}
                style={styles.card}
                onClick={() => navigate(c.path)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow =
                    "0 10px 25px rgba(0,0,0,0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div style={styles.cardTop}>
                  <div style={styles.iconBox}>
                    <Icon size={20} />
                  </div>

                  {c.count !== null && (
                    <div style={styles.count}>
                      <p style={styles.countText}>{c.count}</p>
                    </div>
                  )}
                </div>

                <div>
                  <h3 style={styles.cardTitle}>{c.title}</h3>
                  <p style={styles.cardDesc}>{c.desc}</p>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: "clamp(12px, 4vw, 24px)",
    boxSizing: "border-box",
    maxWidth: "100%",
    overflowX: "hidden",
  },

  header: {
    marginBottom: "20px",
  },

  breadcrumb: {
    fontSize: "12px",
    color: "#94a3b8",
    marginBottom: "6px",
  },

  title: {
    margin: 0,
    fontSize: "26px",
    fontWeight: "600",
  },

  subtitle: {
    color: "#64748b",
    marginTop: "5px",
    fontSize: "14px",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "clamp(12px, 3vw, 18px)",
    width: "100%",
    boxSizing: "border-box",
  },

  card: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "clamp(14px, 3vw, 18px)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    width: "100%",
    boxSizing: "border-box",
  },

  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px",
    flexWrap: "wrap",
  },

  iconBox: {
    background: "#f1f5f9",
    padding: "10px",
    borderRadius: "10px",
    flexShrink: 0,
  },

  count: {
    fontSize: "12px",
    background: "#f1f5f9",
    padding: "6px 8px",
    borderRadius: "6px",
    flexShrink: 0,
  },

  countText: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "500",
  },

  cardTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "600",
  },

  cardDesc: {
    marginTop: "5px",
    fontSize: "13px",
    color: "#64748b",
  },
};
